import type { PriceType } from '~/atoms'

import { useCallback, useMemo, useState } from 'react'
import { Button, ToggleGroup } from '~/components/ui'
import { cn } from '~/utils'
import MapMinMaxFilter from './MinMax'

function getSuffix(priceType: PriceType) {
  switch (priceType) {
    case 'total':
      return ''
    case 'sf':
      return '/SF'
    case 'acre':
      return '/acre'
    default:
      return ''
  }
}

export default function MapSalePriceFilter({
  className,
  filterValues,
  priceType = 'total',
  updateFilter,
}: {
  className?: string
  filterValues: (number | undefined)[]
  priceType?: PriceType
  updateFilter: (updates: Record<string, any>) => void
}) {
  const [resetKey, setResetKey] = useState(Date.now())

  const suffix = useMemo(() => {
    return getSuffix(priceType)
  }, [priceType])

  const handlePriceTypeChange = useCallback(
    (newPriceType: PriceType) => {
      if (newPriceType === priceType) return

      updateFilter({
        priceType: newPriceType,
      })
    },
    [priceType, updateFilter]
  )

  const handlePriceUpdate = useCallback(
    (updates: Record<string, any>) => {
      updateFilter(updates)
    },
    [updateFilter]
  )

  const handleReset = useCallback(() => {
    updateFilter({ prices: undefined, priceType: 'total' })
    setResetKey(Date.now())
  }, [updateFilter])

  const hasValues = filterValues.filter((v) => v).length > 0

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm">
          Sale Price
          {hasValues && (
            <Button
              variant="link"
              size="none"
              className="ml-2"
              onClick={handleReset}>
              (reset)
            </Button>
          )}
        </p>
        <ToggleGroup
          className="h-7"
          onValueChange={(value) => {
            if (value) handlePriceTypeChange(value as PriceType)
          }}
          type="single"
          value={priceType}>
          <ToggleGroup.Item
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            value="sf">
            $/SF
          </ToggleGroup.Item>

          <ToggleGroup.Item
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            value="acre">
            $/acre
          </ToggleGroup.Item>

          <ToggleGroup.Item
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            value="total">
            Total
          </ToggleGroup.Item>
        </ToggleGroup>
      </div>

      <MapMinMaxFilter
        end={suffix}
        filterValues={filterValues}
        hideReset
        key={resetKey}
        label=""
        name="prices"
        start="$"
        updateFilter={handlePriceUpdate}
      />
    </div>
  )
}
